import React, { useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import axios from 'axios';
import posthog from 'posthog-js';
import API_URL from '../../config';
import { mergeSubscriptionTier } from '../../utils/subscription';
import { showError } from '../../utils/toast';
import { SovrynnLogo } from '../Brand';
import '../../App.css';
import '../../pages/legal-pages.css';

const Register = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    firstName: '',
    lastName: '',
    phone: '',
  });
  const [smsConsent, setSmsConsent] = useState(false);
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [focusedFields, setFocusedFields] = useState({
    email: false,
    password: false,
    firstName: false,
    lastName: false,
    phone: false,
  });

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
    setError('');
  };

  const handleFocus = (field) => {
    setFocusedFields((prev) => ({
      ...prev,
      [field]: true,
    }));
  };

  const handleBlur = (field) => {
    setFocusedFields((prev) => ({
      ...prev,
      [field]: formData[field].length > 0,
    }));
  };

  const handleRegister = async (e) => {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    try {
      console.log('Registering user...');

      const phoneTrimmed = formData.phone.trim();
      const response = await axios.post(
        `${API_URL}/api/auth/register`,
        {
          email: formData.email,
          password: formData.password,
          firstName: formData.firstName,
          lastName: formData.lastName,
          phone: phoneTrimmed ? phoneTrimmed : null,
          smsConsent: smsConsent === true,
        }
      );

      console.log('Registration response:', response.data);

      // Save token
      if (response.data.access_token) {
        localStorage.setItem('token', response.data.access_token);
        const user = mergeSubscriptionTier(response.data.user);
        localStorage.setItem('user', JSON.stringify(user));

        posthog.identify(user.email, {
          email: user.email,
          name: user.firstName + ' ' + user.lastName,
          plan: user.subscriptionTier || 'free',
          createdAt: user.createdAt,
        });
        posthog.group('plan', user.subscriptionTier || 'free', {
          name: user.subscriptionTier || 'free',
        });

        posthog.capture('user_registered', {
          plan: user.subscriptionTier || 'free',
        });

        // Redirect to onboarding
        console.log('Redirecting to onboarding...');
        navigate('/onboarding');
      } else {
        throw new Error('No access token received from server');
      }
    } catch (err) {
      const isNetworkError =
        err.code === 'ERR_NETWORK' ||
        err.code === 'ERR_CONNECTION_REFUSED' ||
        err.message === 'Network Error';
      const errorMessage = isNetworkError
        ? 'Cannot reach the server. Make sure the backend is running (npm run start:dev in the project root).'
        : (err.response?.data?.message || err.message || 'Registration failed');
      setError(errorMessage);
      showError(errorMessage);
      if (!isNetworkError) console.error('Registration failed:', err.response?.data?.message || err.message);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="login-container-wrapper register-page">
      <div className="login-container">
        <div className="login-content">
          <div className="auth-logo">
            <SovrynnLogo size="lg" />
          </div>

          <p className="auth-register-tagline">
            DOT compliance intelligence for owner-operators and small fleets.
          </p>

          <form onSubmit={handleRegister} className="login-form">
            <div className="input-group">
              <label
                htmlFor="firstName"
                className={`form-label ${focusedFields.firstName || formData.firstName ? 'focused' : ''}`}
              >
                First Name
              </label>
              <input
                type="text"
                name="firstName"
                id="firstName"
                value={formData.firstName}
                onChange={handleChange}
                onFocus={() => handleFocus('firstName')}
                onBlur={() => handleBlur('firstName')}
                className="form-input"
                placeholder="Jane"
                required
              />
            </div>

            <div className="input-group">
              <label
                htmlFor="lastName"
                className={`form-label ${focusedFields.lastName || formData.lastName ? 'focused' : ''}`}
              >
                Last Name
              </label>
              <input
                type="text"
                name="lastName"
                id="lastName"
                value={formData.lastName}
                onChange={handleChange}
                onFocus={() => handleFocus('lastName')}
                onBlur={() => handleBlur('lastName')}
                className="form-input"
                placeholder="Smith"
                required
              />
            </div>

            <div className="input-group">
              <label
                htmlFor="email"
                className={`form-label ${focusedFields.email || formData.email ? 'focused' : ''}`}
              >
                Email
              </label>
              <input
                type="email"
                name="email"
                id="email"
                value={formData.email}
                onChange={handleChange}
                onFocus={() => handleFocus('email')}
                onBlur={() => handleBlur('email')}
                className="form-input"
                placeholder="jane@example.com"
                required
              />
            </div>

            <div className="input-group">
              <label
                htmlFor="phone"
                className={`form-label ${focusedFields.phone || formData.phone ? 'focused' : ''}`}
              >
                Mobile Number
              </label>
              <input
                type="tel"
                name="phone"
                id="phone"
                value={formData.phone}
                onChange={handleChange}
                onFocus={() => handleFocus('phone')}
                onBlur={() => handleBlur('phone')}
                className="form-input"
                placeholder="+1 (555) 000-0000"
                autoComplete="tel"
              />
            </div>
            <p className="register-field-hint">
              Optional. Used for SMS from Rynn, Sovrynn&apos;s AI assistant.
            </p>
            <label className="register-sms-consent" htmlFor="smsConsent">
              <input
                type="checkbox"
                id="smsConsent"
                name="smsConsent"
                checked={smsConsent}
                onChange={(e) => setSmsConsent(e.target.checked)}
              />
              <span>
                Text me from Rynn, Sovrynn&apos;s AI assistant: compliance alerts (CDL, medical card, inspection deadlines), and let me text questions and photos to get replies and update my files. Msg frequency varies. Msg &amp; data rates may apply. Reply STOP to opt out, HELP for help. Consent is not a condition of purchase.
              </span>
            </label>

            <div className="input-group">
              <label
                htmlFor="password"
                className={`form-label ${focusedFields.password || formData.password ? 'focused' : ''}`}
              >
                Password (min 8 characters)
              </label>
              <input
                type="password"
                name="password"
                id="password"
                value={formData.password}
                onChange={handleChange}
                onFocus={() => handleFocus('password')}
                onBlur={() => handleBlur('password')}
                className="form-input"
                placeholder="Min. 8 characters"
                required
                minLength={8}
              />
            </div>

            {error && <div className="error-message">{error}</div>}

            <button
              type="submit"
              className="submit-button"
              disabled={isLoading}
            >
              {isLoading ? 'Creating account...' : 'Create Account'}
            </button>

            <p className="register-legal-agree">
              By creating an account you agree to our{' '}
              <Link to="/terms">Terms of Service</Link> and <Link to="/privacy">Privacy Policy</Link>.
            </p>

            <div className="register-link">
              <span>Already have an account? </span>
              <Link to="/login" className="register-link-text">
                Sign In
              </Link>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default Register;
